import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type {
    AvailableFields,
    LeadFieldMapping,
} from '@/components/leads/mapping/mappingValidation';
import { effectiveCombineOrder, type CombineOrder } from './fieldMappingConversion';

export interface CombineOrderPanelProps {
    /** Combinable targets currently mapped from more than one source field. */
    targets: string[];
    mapping: LeadFieldMapping;
    combineOrder: CombineOrder;
    availableFields: AvailableFields;
    /** source field key -> display label. */
    sourceFieldLabels: Record<string, string>;
    onMove: (target: string, sourceKey: string, direction: 'up' | 'down') => void;
}

/**
 * Lets the user see and reorder the source fields that are concatenated into
 * one combinable target (e.g. `first_name` + `last_name` -> `name`,
 * field-mapping.md §4.2). The save order is the order values are joined in.
 */
const CombineOrderPanel: React.FC<CombineOrderPanelProps> = ({
    targets,
    mapping,
    combineOrder,
    availableFields,
    sourceFieldLabels,
    onMove,
}) => {
    const { t } = useTranslation('crm');

    if (targets.length === 0) return null;

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">
                    {t('campaigns.editor.mapping.combineTitle')}
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                    {t('campaigns.editor.mapping.combineDescription')}
                </p>
            </CardHeader>
            <CardContent className="space-y-4">
                {targets.map(target => {
                    const order = effectiveCombineOrder(mapping, combineOrder, target);
                    const targetLabel = availableFields[target]?.label ?? target;

                    return (
                        <div key={target} className="rounded-lg border p-3">
                            <p className="mb-2 text-sm font-medium">{targetLabel}</p>
                            <ol className="space-y-1">
                                {order.map((sourceKey, index) => (
                                    <li
                                        key={sourceKey}
                                        className="flex items-center justify-between rounded-md bg-muted/50 px-3 py-1.5 text-sm"
                                    >
                                        <span>
                                            {index + 1}. {sourceFieldLabels[sourceKey] ?? sourceKey}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <Button
                                                variant="ghost"
                                                size="icon-sm"
                                                disabled={index === 0}
                                                onClick={() => onMove(target, sourceKey, 'up')}
                                                aria-label={t('campaigns.editor.mapping.moveUp')}
                                            >
                                                <ArrowUp className="h-3.5 w-3.5" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon-sm"
                                                disabled={index === order.length - 1}
                                                onClick={() => onMove(target, sourceKey, 'down')}
                                                aria-label={t('campaigns.editor.mapping.moveDown')}
                                            >
                                                <ArrowDown className="h-3.5 w-3.5" />
                                            </Button>
                                        </span>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    );
                })}
            </CardContent>
        </Card>
    );
};

export default CombineOrderPanel;
